/**
 * OptionSelector — Unified option picker for settings screens.
 *
 * Three layouts, zero style overrides. All visual rules are enforced.
 *
 * - segmented: iOS-style segmented control with shared background.
 *   For 2-5 short-label options. Equal-width buttons.
 * - card: Vertical full-width cards. For long labels or subtitles.
 * - pill: Horizontal pills that wrap. Fallback for edge cases.
 *
 * Label is always on top: FS_SM, textSecondary, bold.
 */

import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { VStack, Box, Text, Pressable } from '@gluestack-ui/themed';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { FS_XS, FS_SM, FS_MD } from '../../theme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OptionItem<T extends string = string> {
  value: T;
  label: string;
  /** Secondary line of text, shown only in 'card' layout */
  subtitle?: string;
}

export type OptionLayout = 'segmented' | 'card' | 'pill';

export interface OptionSelectorProps<T extends string = string> {
  /** Visible label text */
  label: string;
  /** Available options */
  options: OptionItem<T>[];
  /** Currently selected value */
  value: T;
  /** Selection callback */
  onChange: (value: T) => void;
  /** Layout variant. Default: 'segmented' */
  layout?: OptionLayout;
  /** Hint text shown below the label */
  hint?: string;
  /** Prefix for testID generation. Each option gets `${testID}-${value}` */
  testID: string;
  /** Disabled state */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Internal: SegmentedOption
// ---------------------------------------------------------------------------

interface SegmentedOptionProps {
  item: OptionItem;
  isActive: boolean;
  fontScale: number;
  ffs: Record<string, string> | undefined;
  theme: ReturnType<typeof useDisplayTheme>['theme'];
  testID: string;
  groupLabel: string;
  disabled?: boolean;
  onPress: () => void;
}

const SegmentedOption = React.memo(function SegmentedOption({
  item,
  isActive,
  fontScale,
  ffs,
  theme,
  testID,
  groupLabel,
  disabled,
  onPress,
}: SegmentedOptionProps) {
  return (
    <TouchableOpacity
      testID={testID}
      accessibilityLabel={`${groupLabel}: ${item.label}`}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      disabled={disabled}
      onPress={onPress}
      activeOpacity={0.7}
      style={{
        flex: 1,
        paddingVertical: 8,
        borderRadius: 8,
        backgroundColor: isActive ? theme.primary : 'transparent',
        alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Text
        allowFontScaling={false}
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.7}
        style={{
          fontSize: FS_SM * fontScale,
          fontWeight: '600',
          color: isActive ? '#FFFFFF' : theme.textSecondary,
          ...ffs,
        }}
      >
        {item.label}
      </Text>
      {/* E2E selected-state marker — a 1x1 element present only on the active
          option, so tests can assert WHICH option is selected via testID
          (`${testID}-selected`). The option's own testID stays stable for taps. */}
      {isActive && (
        <View
          testID={`${testID}-selected`}
          style={{ position: 'absolute', width: 1, height: 1 }}
        />
      )}
    </TouchableOpacity>
  );
});

// ---------------------------------------------------------------------------
// Internal: OptionPill
// ---------------------------------------------------------------------------

interface OptionPillProps {
  item: OptionItem;
  isActive: boolean;
  fontScale: number;
  ffs: Record<string, string> | undefined;
  theme: ReturnType<typeof useDisplayTheme>['theme'];
  testID: string;
  groupLabel: string;
  disabled?: boolean;
  onPress: () => void;
}

const OptionPill = React.memo(function OptionPill({
  item,
  isActive,
  fontScale,
  ffs,
  theme,
  testID,
  groupLabel,
  disabled,
  onPress,
}: OptionPillProps) {
  return (
    <Pressable
      testID={testID}
      accessibilityLabel={`${groupLabel}: ${item.label}`}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      disabled={disabled}
      onPress={onPress}
    >
      <Box
        px="$3"
        py="$2"
        rounded="$full"
        style={{
          backgroundColor: isActive ? theme.primary : theme.inputBg,
          borderWidth: 1,
          borderColor: isActive ? theme.primary : theme.border,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_SM * fontScale,
            fontWeight: isActive ? '600' : 'normal',
            color: isActive ? '#FFFFFF' : theme.text,
            ...ffs,
          }}
        >
          {item.label}
        </Text>
        {isActive && (
          <View
            testID={`${testID}-selected`}
            style={{ position: 'absolute', width: 1, height: 1 }}
          />
        )}
      </Box>
    </Pressable>
  );
});

// ---------------------------------------------------------------------------
// Internal: OptionCard
// ---------------------------------------------------------------------------

interface OptionCardProps {
  item: OptionItem;
  isActive: boolean;
  fontScale: number;
  ffs: Record<string, string> | undefined;
  theme: ReturnType<typeof useDisplayTheme>['theme'];
  activeRowBg: string;
  testID: string;
  groupLabel: string;
  disabled?: boolean;
  onPress: () => void;
}

const OptionCard = React.memo(function OptionCard({
  item,
  isActive,
  fontScale,
  ffs,
  theme,
  activeRowBg,
  testID,
  groupLabel,
  disabled,
  onPress,
}: OptionCardProps) {
  return (
    <Pressable
      testID={testID}
      accessibilityLabel={`${groupLabel}: ${item.label}`}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      disabled={disabled}
      onPress={onPress}
    >
      <Box
        p="$3"
        rounded="$lg"
        style={{
          backgroundColor: isActive ? activeRowBg : theme.inputBg,
          borderWidth: 1,
          borderColor: isActive ? theme.primary : theme.border,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_MD * fontScale,
            fontWeight: isActive ? '600' : '400',
            color: isActive ? theme.primary : theme.text,
            ...ffs,
          }}
        >
          {item.label}
        </Text>
        {item.subtitle != null && (
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_XS * fontScale,
              color: theme.textSecondary,
              marginTop: 2,
              ...ffs,
            }}
          >
            {item.subtitle}
          </Text>
        )}
        {isActive && (
          <View
            testID={`${testID}-selected`}
            style={{ position: 'absolute', width: 1, height: 1 }}
          />
        )}
      </Box>
    </Pressable>
  );
});

// ---------------------------------------------------------------------------
// OptionSelector
// ---------------------------------------------------------------------------

function OptionSelectorInner<T extends string = string>({
  label,
  options,
  value,
  onChange,
  layout = 'segmented',
  hint,
  testID,
  disabled,
}: OptionSelectorProps<T>) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const activeRowBg = theme.isDark ? '#1E3A5F' : '#EBF5FF';

  const renderOptions = () => {
    if (layout === 'segmented') {
      return (
        <View
          style={{
            flexDirection: 'row',
            borderRadius: 10,
            backgroundColor: theme.inputBg,
            padding: 3,
          }}
        >
          {options.map((opt) => (
            <SegmentedOption
              key={opt.value}
              item={opt}
              isActive={value === opt.value}
              fontScale={fontScale}
              ffs={ffs}
              theme={theme}
              testID={`${testID}-${opt.value}`}
              groupLabel={label}
              disabled={disabled}
              onPress={() => onChange(opt.value)}
            />
          ))}
        </View>
      );
    }

    if (layout === 'card') {
      return (
        <View style={{ gap: 8 }}>
          {options.map((opt) => (
            <OptionCard
              key={opt.value}
              item={opt}
              isActive={value === opt.value}
              fontScale={fontScale}
              ffs={ffs}
              theme={theme}
              activeRowBg={activeRowBg}
              testID={`${testID}-${opt.value}`}
              groupLabel={label}
              disabled={disabled}
              onPress={() => onChange(opt.value)}
            />
          ))}
        </View>
      );
    }

    // pill
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {options.map((opt) => (
          <OptionPill
            key={opt.value}
            item={opt}
            isActive={value === opt.value}
            fontScale={fontScale}
            ffs={ffs}
            theme={theme}
            testID={`${testID}-${opt.value}`}
            groupLabel={label}
            disabled={disabled}
            onPress={() => onChange(opt.value)}
          />
        ))}
      </View>
    );
  };

  return (
    <VStack space="sm">
      {/* Label — always FS_SM, textSecondary, bold */}
      <Text
        allowFontScaling={false}
        style={{
          fontSize: FS_SM * fontScale,
          fontWeight: '600',
          color: theme.textSecondary,
          ...ffs,
        }}
      >
        {label}
      </Text>

      {/* Hint (optional) */}
      {hint != null && (
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_XS * fontScale,
            color: theme.textSecondary,
            ...ffs,
          }}
        >
          {hint}
        </Text>
      )}

      {renderOptions()}
    </VStack>
  );
}

export const OptionSelector = React.memo(OptionSelectorInner) as typeof OptionSelectorInner;
